import React from 'react';
import { useTranslation } from 'react-i18next';
import { format as formatDate, parseISO } from 'date-fns';
import { ArrowDown, ArrowUp, ArrowUpDown, Search } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  formatUnits,
  formatRateAdaptive,
  formatDaysOfCover,
  daysOfCoverBand,
  BUYING_ACTION_PRIORITY,
  RAG_TEXT,
} from '../../lib/reporting-format';
import { buyingActionLabel, type ReorderSignalRow } from './types';
import { BuyingActionBadge } from '../BuyingActionBadge';

type SortField =
  | 'action'
  | 'sku'
  | 'onHand'
  | 'runRate'
  | 'daysOfCover'
  | 'incoming'
  | 'projectedDaysOfCover'
  | 'nextEtaDate';
type SortDir = 'asc' | 'desc';

const ALL_CATEGORIES = '__all__';
const ALL_ACTIONS = '__all_actions__';

/** Short, locale-formatted ETA ("17 Jun"); em-dash when unknown. */
function formatEta(iso: string | null): string {
  if (!iso) return '-';
  try {
    return formatDate(parseISO(iso), 'd MMM');
  } catch {
    return iso;
  }
}

interface OnOrderReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rows: ReorderSignalRow[];
}

/**
 * Full, dice-able buying list. The owner's question is "what do I order now vs
 * what's already covered" — so the default sort is buying urgency (order_now
 * first), then fewest days of cover. Search by SKU/category, filter by action +
 * category, sort any column. Mirrors BlankConsumptionReportDialog.
 */
export const OnOrderReportDialog: React.FC<OnOrderReportDialogProps> = ({
  open,
  onOpenChange,
  rows,
}) => {
  const { t } = useTranslation('inventory');

  const [query, setQuery] = React.useState('');
  const [category, setCategory] = React.useState<string>(ALL_CATEGORIES);
  const [action, setAction] = React.useState<string>(ALL_ACTIONS);
  const [sortField, setSortField] = React.useState<SortField>('action');
  const [sortDir, setSortDir] = React.useState<SortDir>('asc');

  // Reset to the urgency-first default each time the report opens.
  React.useEffect(() => {
    if (open) {
      setQuery('');
      setCategory(ALL_CATEGORIES);
      setAction(ALL_ACTIONS);
      setSortField('action');
      setSortDir('asc');
    }
  }, [open]);

  const categories = React.useMemo(() => {
    const set = new Set<string>();
    for (const row of rows) {
      if (row.category) set.add(row.category);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((row) => {
      if (category !== ALL_CATEGORIES && row.category !== category) return false;
      if (action !== ALL_ACTIONS && row.buyingAction !== action) return false;
      if (!q) return true;
      const haystack = `${row.sku} ${row.category ?? ''}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [rows, query, category, action]);

  const sorted = React.useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'action':
          // Primary: urgency. Tie-break: fewest days of cover (nulls sink).
          cmp = BUYING_ACTION_PRIORITY[a.buyingAction] - BUYING_ACTION_PRIORITY[b.buyingAction];
          if (cmp === 0) {
            const av = a.daysOfCover ?? Number.POSITIVE_INFINITY;
            const bv = b.daysOfCover ?? Number.POSITIVE_INFINITY;
            cmp = av - bv;
          }
          break;
        case 'sku':
          cmp = a.sku.localeCompare(b.sku);
          break;
        case 'onHand':
          cmp = a.onHand - b.onHand;
          break;
        case 'runRate':
          cmp = a.runRate - b.runRate;
          break;
        case 'incoming':
          cmp = a.incoming - b.incoming;
          break;
        case 'daysOfCover': {
          const av = a.daysOfCover ?? Number.POSITIVE_INFINITY;
          const bv = b.daysOfCover ?? Number.POSITIVE_INFINITY;
          cmp = av - bv;
          break;
        }
        case 'projectedDaysOfCover': {
          const av = a.projectedDaysOfCover ?? Number.POSITIVE_INFINITY;
          const bv = b.projectedDaysOfCover ?? Number.POSITIVE_INFINITY;
          cmp = av - bv;
          break;
        }
        case 'nextEtaDate': {
          // Unknown ETA sinks regardless of direction.
          const av = a.nextEtaDate ? Date.parse(a.nextEtaDate) : Number.POSITIVE_INFINITY;
          const bv = b.nextEtaDate ? Date.parse(b.nextEtaDate) : Number.POSITIVE_INFINITY;
          cmp = av - bv;
          break;
        }
      }
      return cmp * dir;
    });
  }, [filtered, sortField, sortDir]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      // Action + SKU default ascending; numeric columns biggest-first.
      setSortDir(field === 'action' || field === 'sku' ? 'asc' : 'desc');
    }
  };

  const SortIcon: React.FC<{ field: SortField }> = ({ field }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="ml-1 h-3 w-3 opacity-40" aria-hidden="true" />;
    }
    return sortDir === 'asc' ? (
      <ArrowUp className="ml-1 h-3 w-3" aria-hidden="true" />
    ) : (
      <ArrowDown className="ml-1 h-3 w-3" aria-hidden="true" />
    );
  };

  const SortHeader: React.FC<{
    field: SortField;
    label: string;
    align?: 'left' | 'right';
  }> = ({ field, label, align = 'right' }) => (
    <th
      className={cn(
        'whitespace-nowrap py-2 px-3 font-medium',
        align === 'right' ? 'text-right' : 'text-left',
      )}
    >
      <button
        type="button"
        onClick={() => handleSort(field)}
        aria-sort={sortField === field ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
        className={cn(
          'inline-flex items-center rounded-sm hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          align === 'right' && 'justify-end',
        )}
      >
        {label}
        <SortIcon field={field} />
      </button>
    </th>
  );

  const isFiltered =
    query.trim() !== '' || category !== ALL_CATEGORIES || action !== ALL_ACTIONS;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] w-[95vw] max-w-6xl flex-col gap-0 p-0">
        <DialogHeader className="border-b px-6 pb-4 pt-6">
          <DialogTitle>{t('reporting.v2.buyingSignals', 'Buying signals')}</DialogTitle>
          <DialogDescription>
            {t(
              'reporting.v2.buyingReportDesc',
              'Every at-risk SKU cross-referenced against inbound stock. Sorted most urgent first.',
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Dice controls */}
        <div className="flex flex-col gap-3 border-b px-6 py-4 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('reporting.v2.searchSkuCategory', 'Search SKU or category')}
              className="pl-9"
              aria-label={t('reporting.v2.searchSkuCategory', 'Search SKU or category')}
            />
          </div>
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger className="sm:w-48" aria-label={t('reporting.v2.filterAction', 'Filter by action')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_ACTIONS}>
                {t('reporting.v2.allActions', 'All actions')}
              </SelectItem>
              <SelectItem value="order_now">{buyingActionLabel('order_now', t)}</SelectItem>
              <SelectItem value="arriving_late">{buyingActionLabel('arriving_late', t)}</SelectItem>
              <SelectItem value="covered">{buyingActionLabel('covered', t)}</SelectItem>
            </SelectContent>
          </Select>
          {categories.length > 0 && (
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="sm:w-48" aria-label={t('reporting.v2.filterCategory', 'Filter by category')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_CATEGORIES}>
                  {t('reporting.v2.allCategories', 'All categories')}
                </SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    {cat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Row count */}
        <div className="flex items-center justify-between px-6 py-2.5 text-xs text-muted-foreground">
          <span className="tabular-nums">
            {t('reporting.v2.showingNofM', 'Showing {{shown}} of {{total}}', {
              shown: sorted.length,
              total: rows.length,
            })}
          </span>
        </div>

        {/* Table */}
        <div className="min-h-0 flex-1 overflow-auto border-t">
          <table className="w-full min-w-[52rem] border-collapse text-sm">
            <thead className="sticky top-0 z-10 bg-card text-xs text-muted-foreground">
              <tr className="border-b">
                <SortHeader field="sku" label={t('reporting.v2.sku', 'SKU')} align="left" />
                <SortHeader field="onHand" label={t('reporting.v2.onHand', 'On hand')} />
                <SortHeader field="runRate" label={t('reporting.v2.runRatePerDay', 'Run-rate/day')} />
                <SortHeader field="daysOfCover" label={t('reporting.v2.cover', 'Cover')} />
                <SortHeader field="incoming" label={t('reporting.v2.incoming', 'Incoming')} />
                <SortHeader
                  field="projectedDaysOfCover"
                  label={t('reporting.v2.projectedCover', 'Projected')}
                />
                <SortHeader field="nextEtaDate" label={t('reporting.v2.nextEta', 'Next ETA')} />
                <SortHeader field="action" label={t('reporting.v2.action', 'Action')} />
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-sm text-muted-foreground">
                    {isFiltered
                      ? t('reporting.v2.noSignalMatch', 'No SKUs match your search or filter.')
                      : t('reporting.v2.noSignals', 'Nothing needs reordering right now.')}
                  </td>
                </tr>
              ) : (
                sorted.map((row) => {
                  const coverBand = daysOfCoverBand(row.daysOfCover);
                  const projBand = daysOfCoverBand(row.projectedDaysOfCover);
                  return (
                    <tr key={`${row.skuId}-${row.locationId}`} className="border-b last:border-0 hover:bg-muted/40">
                      <td className="py-2.5 px-3">
                        <p className="font-mono text-xs font-medium">{row.sku}</p>
                        {row.category && (
                          <p className="text-xs text-muted-foreground">{row.category}</p>
                        )}
                      </td>
                      <td className="py-2.5 px-3 text-right tabular-nums">
                        {formatUnits(row.onHand)}
                        {row.notifyThreshold != null && (
                          <span className="text-xs font-normal text-muted-foreground">
                            {' / '}
                            {formatUnits(row.notifyThreshold)}
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 px-3 text-right tabular-nums">
                        {formatRateAdaptive(row.runRate)}
                      </td>
                      <td className={cn('py-2.5 px-3 text-right font-semibold tabular-nums', RAG_TEXT[coverBand])}>
                        {formatDaysOfCover(row.daysOfCover)}
                      </td>
                      <td className="py-2.5 px-3 text-right tabular-nums">
                        {row.incoming > 0 ? formatUnits(row.incoming) : '-'}
                      </td>
                      <td className={cn('py-2.5 px-3 text-right font-semibold tabular-nums', RAG_TEXT[projBand])}>
                        {formatDaysOfCover(row.projectedDaysOfCover)}
                      </td>
                      <td className="py-2.5 px-3 text-right tabular-nums text-muted-foreground">
                        {formatEta(row.nextEtaDate)}
                      </td>
                      <td className="py-2.5 px-3 text-right">
                        <BuyingActionBadge action={row.buyingAction} />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
};
